/**
 * 홈 「매출 분석」대시보드 — 5개 그리드용 API 경로.
 * 호출은 `src/api/common.js`의 `api2` 기준 상대경로입니다.
 *
 * **공통 요청 바디**(5개 전부 동일): `GROUP_CD`, `STORE_CD`, `SEQUENCE`, `FROM_DT`, `TO_DT` (`yyyy-mm-dd` 권장)
 * — `buildSalesAnalysisDashBody` / `getWeeklyKeyIndicators` 등은 `src/api/common.js` 참고.
 *
 * **vewStoreSales 매출**: 5개 그리드·프로시저는 동일 의미로 **`lngActAmt`(실매출)** 집계를 권장합니다.
 * (`lngSalAmt`와 혼용하면 주간 합계·매장 매출 합계·객단가가 서로 맞지 않을 수 있습니다.)
 *
 * 예) api2.post(SALES_ANALYSIS_DASHBOARD_API.weeklyKeyIndicators, { … })
 *
 * 백엔드 asmx·메서드명은 프로젝트 규칙에 맞게 수정하면 됩니다.
 * (기존 `salesDash5001.asmx` 등 → `MISALES/SLS00_001DASH.asmx` 로 맞추거나 경로만 이 상수와 통일)
 */

/**
 * 상세 페이지 탭 열기 — 메뉴의 `lngProgramID`·XML 경로와 일치해야 합니다.
 * @type {{ strUrl: string; lngProgramID: number; strTitle: string }}
 */
export const SALES_ANALYSIS_DASHBOARD_DETAIL_TAB = {
  strUrl: "MISALE::SLS14_032RPT.xml",
  lngProgramID: 0,
  strTitle: "매출 분석 상세",
};

/**
 * 홈 매출 분석 —「매출목표 등록」·「목표원가율 등록」→ `MISALES/SLS01_001INS.xml`
 * - `minorCategory`에서 `SLS01_001INS.xml` 조회 후 `moveOtherTab`(좌측 메뉴와 동일 탭)으로 열림.
 * - 메뉴에 없으면 `lngProgramID`·strUrl 폴백 사용, 0이면 라우터 직접 이동.
 * - 목표원가율도 동일 화면(달력 상단)에서 등록.
 * @type {{ strUrl: string; lngProgramID: number; strTitle: string }}
 */
export const SALES_GOAL_REGISTRATION_TAB = {
  strUrl: "MISALE::SLS01_001INS.xml",
  lngProgramID: 0,
  strTitle: "매출목표 등록",
};

/**
 * 홈에서 매출 분석 대시보드 카드를 띄울 규칙.
 * - 마키노차야(1871): `lngUserAdminID` 가 10, 30, 또는 90 이상
 *   (`lngUserAdminID === lngStoreGroup` 그룹관리자는 홈·API에서 이미 스킵, 1871=1871 포함)
 * - 테스트(5001): 기존과 동일, 92만
 * - 그 외 그룹: 이 대시보드 미사용
 * 신규 대시보드 5개 API 호출 여부는 `SalesAnalysisDashboard`의 `shouldSkipSalesAnalysisDashApis`만 따르며, 홈에 카드를 띄울지와는 별개(컴포넌트가 마운트될 때만 해당 API 실행).
 * (제거 시 `homePage2`·`SalesAnalysisDashboard` 분기 함께 정리)
 */
export const SALES_ANALYSIS_HOME_GROUP_1871 = "1871";
export const SALES_ANALYSIS_HOME_GROUP_TEST = "5001";

/** 테스트(5001) 기존 지정 계정. 마키노차야는 `matchesSalesAnalysisHomeTempBypass` 의 10 / 30 / 90 이상 규칙. */
export const SALES_ANALYSIS_HOME_TEMP_BYPASS_RULES = Object.freeze([
  Object.freeze({
    lngStoreGroup: SALES_ANALYSIS_HOME_GROUP_TEST,
    lngUserAdminIDs: Object.freeze([92]),
  }),
]);

/**
 * 세션이 홈 매출 분석 대시보드 노출 조건과 일치하는지
 * @param {Record<string, unknown>|unknown} u userData
 */
export function matchesSalesAnalysisHomeTempBypass(u) {
  if (!u || typeof u !== "object" || Array.isArray(u)) return false;
  const grp = String(/** @type {{ lngStoreGroup?: unknown }} */ (u).lngStoreGroup ?? "");
  const aid = Number(/** @type {{ lngUserAdminID?: unknown }} */ (u).lngUserAdminID);
  if (!Number.isFinite(aid)) return false;
  if (String(aid) === grp) return false;
  if (grp === SALES_ANALYSIS_HOME_GROUP_1871) {
    return aid === 10 || aid === 30 || aid >= 90;
  }
  return SALES_ANALYSIS_HOME_TEMP_BYPASS_RULES.some(
    (r) => r.lngStoreGroup === grp && r.lngUserAdminIDs.includes(aid)
  );
}

export const SALES_ANALYSIS_DASHBOARD_API = {
  /** 1. 주간 핵심 지표 — Body: GROUP_CD, STORE_CD, SEQUENCE, FROM_DT, TO_DT */
  weeklyKeyIndicators: "/MISALES/SLS00_001DASH.asmx/getWeeklyKeyIndicators",

  /**
   * 2. 객수 / 객단가 — 매장별
   * Body: GROUP_CD, STORE_CD(본사 0), SEQUENCE, FROM_DT, TO_DT (`yyyy-mm-dd` 권장)
   */
  customerAndUnitPriceByStore:
    "/MISALES/SLS00_001DASH.asmx/getCustomerAndUnitPriceByStore",

  /** 3. 매장 매출 — 매장별 (동일 Body) */
  storeSalesByStore: "/MISALES/SLS00_001DASH.asmx/getStoreSalesByStore",

  /** 4. 재료비 — 매장별 (동일 Body) */
  materialCostByStore: "/MISALES/SLS00_001DASH.asmx/getMaterialCostByStore",

  /**
   * 5. 예약현황 — 매장별 (동일 Body; SP `usp_VUE_saGetReservationByStore`)
   * 구 인건비 `getLaborCostByStore` 대체
   */
  reservationByStore: "/MISALES/SLS00_001DASH.asmx/getReservationByStore",

  /** @deprecated getReservationByStore 사용 */
  reservationStatusByStore: "/MISALES/SLS00_001DASH.asmx/getReservationByStore",

  /** @deprecated 예약현황으로 교체 — reservationByStore 사용 */
  laborCostByStore: "/MISALES/SLS00_001DASH.asmx/getLaborCostByStore",
};
